import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign, TrendingUp, TrendingDown, PieChart, BarChart3,
  Calendar, Download, FileSpreadsheet, Building, Users,
  CreditCard, Receipt, Wallet, ArrowUpRight, ArrowDownRight,
  Filter, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle,
  CheckCircle2, Clock, Target, Landmark, Calculator, BriefcaseBusiness,
  Plus, FileText, Send, Eye, Edit, Trash2, Search, BookOpen,
  ArrowRightLeft, Building2, Banknote, Scale
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Separator } from '../../ui/separator';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Progress } from '../../ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, BarChart, Bar, Legend, LineChart, Line
} from 'recharts';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { getMonthName, getCurrentYear } from '../../../lib/dateUtils';
import { exportToCSV } from '../../../lib/export';
import { toast } from 'sonner';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

const CHART_COLORS = ['#14b8a6', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];

export default function FinanceDashboard() {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [taxLiabilities, setTaxLiabilities] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [compensation, setCompensation] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  
  // Chart of Accounts state
  const [chartOfAccounts, setChartOfAccounts] = useState([
    { id: '1000', name: 'Cash', type: 'Asset', subtype: 'Current Asset', balance: 0 },
    { id: '1100', name: 'Accounts Receivable', type: 'Asset', subtype: 'Current Asset', balance: 0 },
    { id: '1200', name: 'Prepaid Expenses', type: 'Asset', subtype: 'Current Asset', balance: 0 },
    { id: '1500', name: 'Fixed Assets', type: 'Asset', subtype: 'Fixed Asset', balance: 0 },
    { id: '2000', name: 'Accounts Payable', type: 'Liability', subtype: 'Current Liability', balance: 0 },
    { id: '2100', name: 'Accrued Expenses', type: 'Liability', subtype: 'Current Liability', balance: 0 },
    { id: '2200', name: 'Payroll Liabilities', type: 'Liability', subtype: 'Current Liability', balance: 0 },
    { id: '2500', name: 'Long-term Debt', type: 'Liability', subtype: 'Long-term Liability', balance: 0 },
    { id: '3000', name: 'Owner\'s Equity', type: 'Equity', subtype: 'Equity', balance: 0 },
    { id: '3100', name: 'Retained Earnings', type: 'Equity', subtype: 'Retained Earnings', balance: 0 },
    { id: '4000', name: 'Service Revenue', type: 'Income', subtype: 'Revenue', balance: 0 },
    { id: '4100', name: 'Product Sales', type: 'Income', subtype: 'Revenue', balance: 0 },
    { id: '5000', name: 'Salaries & Wages', type: 'Expense', subtype: 'Payroll', balance: 0 },
    { id: '5100', name: 'Payroll Taxes', type: 'Expense', subtype: 'Payroll', balance: 0 },
    { id: '5200', name: 'Employee Benefits', type: 'Expense', subtype: 'Payroll', balance: 0 },
    { id: '6000', name: 'Rent Expense', type: 'Expense', subtype: 'Operating', balance: 0 },
    { id: '6100', name: 'Utilities', type: 'Expense', subtype: 'Operating', balance: 0 },
    { id: '6200', name: 'Office Supplies', type: 'Expense', subtype: 'Operating', balance: 0 },
    { id: '6300', name: 'Insurance', type: 'Expense', subtype: 'Operating', balance: 0 }
  ]);
  
  // Transactions/Journal Entries state
  const [transactions, setTransactions] = useState([]);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    entries: [
      { account: '', debit: 0, credit: 0 },
      { account: '', debit: 0, credit: 0 }
    ]
  });
  
  // Invoices & Bills state
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer: '',
    amount: '',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: '',
    status: 'pending'
  });
  const [newBill, setNewBill] = useState({
    vendor: '',
    amount: '',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: '',
    category: '',
    status: 'pending'
  });
  
  // Account management state
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({
    id: '',
    name: '',
    type: 'Asset',
    subtype: '',
    balance: 0
  });
  const [accountSearch, setAccountSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [
        payslipsData,
        runsData,
        taxData,
        bonusData,
        usersData,
        compData
      ] = await Promise.all([
        db.payslips.toArray(),
        db.payrollRuns.toArray(),
        db.taxLiabilities.toArray(),
        db.bonuses.toArray(),
        db.users.toArray(),
        db.employeeCompensation.toArray()
      ]);

      setPayslips(payslipsData);
      setPayrollRuns(runsData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      setTaxLiabilities(taxData);
      setBonuses(bonusData);
      setUsers(usersData.filter(u => u.role !== 'admin'));
      setCompensation(compData);
    } catch (error) {
      console.error('Error loading finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  // Filter payslips by year and quarter
  const filteredPayslips = useMemo(() => {
    let filtered = payslips.filter(p => p.year === selectedYear);
    if (selectedQuarter !== 'all') {
      const q = parseInt(selectedQuarter);
      const quarterMonths = {
        1: [1, 2, 3],
        2: [4, 5, 6],
        3: [7, 8, 9],
        4: [10, 11, 12]
      };
      filtered = filtered.filter(p => quarterMonths[q].includes(p.month));
    }
    return filtered;
  }, [payslips, selectedYear, selectedQuarter]);

  // Calculate totals
  const totals = useMemo(() => {
    const grossPay = filteredPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || 0), 0);
    const netPay = filteredPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0);
    const totalTaxes = filteredPayslips.reduce((sum, p) => sum + (Number(p.totalTaxes) || 0), 0);
    const totalPreTax = filteredPayslips.reduce((sum, p) => sum + (Number(p.totalPreTax) || 0), 0);
    const totalPostTax = filteredPayslips.reduce((sum, p) => sum + (Number(p.totalPostTax) || 0), 0);
    
    // Calculate employer taxes (SS + Medicare match)
    const employerTaxes = filteredPayslips.reduce((sum, p) => {
      const ss = Number(p.taxes?.socialSecurity) || 0;
      const medicare = Number(p.taxes?.medicare) || 0;
      return sum + ss + medicare; // Employer matches these
    }, 0);

    const totalBonuses = bonuses
      .filter(b => b.status === 'approved' && new Date(b.appliedAt).getFullYear() === selectedYear)
      .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

    // Total labor cost = gross + employer taxes
    const totalLaborCost = grossPay + employerTaxes;

    return {
      grossPay,
      netPay,
      totalTaxes,
      totalPreTax,
      totalPostTax,
      employerTaxes,
      totalBonuses,
      totalLaborCost,
      employeeCount: new Set(filteredPayslips.map(p => p.userId)).size,
      payrollCount: filteredPayslips.length
    };
  }, [filteredPayslips, bonuses, selectedYear]);

  // Monthly breakdown
  const monthlyData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    return months.map(month => {
      const monthPayslips = payslips.filter(p => p.year === selectedYear && p.month === month);
      const grossPay = monthPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || 0), 0);
      const taxes = monthPayslips.reduce((sum, p) => sum + (Number(p.totalTaxes) || 0), 0);
      const benefits = monthPayslips.reduce((sum, p) => sum + (Number(p.totalPreTax) || 0), 0);
      const employerTaxes = monthPayslips.reduce((sum, p) => {
        return sum + (Number(p.taxes?.socialSecurity) || 0) + (Number(p.taxes?.medicare) || 0);
      }, 0);

      return {
        month: getMonthName(month).substring(0, 3),
        fullMonth: getMonthName(month),
        grossPay,
        taxes,
        benefits,
        employerTaxes,
        totalCost: grossPay + employerTaxes,
        employees: monthPayslips.length
      };
    });
  }, [payslips, selectedYear]);

  // Expense breakdown by category
  const expenseBreakdown = useMemo(() => {
    const salaries = filteredPayslips.reduce((sum, p) => sum + (Number(p.earnings?.regular) || Number(p.grossPay) || 0), 0);
    const overtime = filteredPayslips.reduce((sum, p) => sum + (Number(p.earnings?.overtime) || 0), 0);
    const federalTaxes = filteredPayslips.reduce((sum, p) => sum + (Number(p.taxes?.federalIncome) || 0), 0);
    const stateTaxes = filteredPayslips.reduce((sum, p) => sum + (Number(p.taxes?.stateIncome) || 0), 0);
    const fica = filteredPayslips.reduce((sum, p) => {
      const ss = (Number(p.taxes?.socialSecurity) || 0) * 2; // Employee + Employer
      const med = (Number(p.taxes?.medicare) || 0) * 2;
      return sum + ss + med;
    }, 0);
    const benefits = filteredPayslips.reduce((sum, p) => sum + (Number(p.totalPreTax) || 0), 0);
    const approvedBonuses = bonuses
      .filter(b => b.status === 'approved' && new Date(b.appliedAt).getFullYear() === selectedYear)
      .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

    return [
      { name: 'Base Salaries', value: salaries, color: '#14b8a6' },
      { name: 'Overtime', value: overtime, color: '#f59e0b' },
      { name: 'FICA (SS + Medicare)', value: fica, color: '#ef4444' },
      { name: 'Federal Tax Withholding', value: federalTaxes, color: '#8b5cf6' },
      { name: 'State Tax Withholding', value: stateTaxes, color: '#0ea5e9' },
      { name: 'Benefits (Pre-tax)', value: benefits, color: '#22c55e' },
      { name: 'Bonuses', value: approvedBonuses, color: '#ec4899' }
    ].filter(e => e.value > 0);
  }, [filteredPayslips, bonuses, selectedYear]);

  // Department breakdown
  const departmentBreakdown = useMemo(() => {
    const deptMap = {};
    filteredPayslips.forEach(p => {
      const user = users.find(u => u.id === p.userId);
      const dept = user?.profile?.department || 'Unassigned';
      if (!deptMap[dept]) {
        deptMap[dept] = { gross: 0, net: 0, taxes: 0, employees: new Set(), count: 0 };
      }
      deptMap[dept].gross += Number(p.grossPay) || 0;
      deptMap[dept].net += Number(p.netPay) || 0;
      deptMap[dept].taxes += Number(p.totalTaxes) || 0;
      deptMap[dept].employees.add(p.userId);
      deptMap[dept].count++;
    });

    return Object.entries(deptMap).map(([name, data]) => ({
      name,
      gross: data.gross,
      net: data.net,
      taxes: data.taxes,
      headcount: data.employees.size,
      avgSalary: data.employees.size > 0 ? data.gross / data.count : 0
    })).sort((a, b) => b.gross - a.gross);
  }, [filteredPayslips, users]);

  // Tax liabilities summary
  const taxSummary = useMemo(() => {
    const pending = taxLiabilities.filter(t => t.status === 'pending');
    const paid = taxLiabilities.filter(t => t.status === 'paid');
    const overdue = taxLiabilities.filter(t => t.status === 'pending' && t.dueDate < Date.now());

    return {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
      paidCount: paid.length,
      paidAmount: paid.reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
      overdueCount: overdue.length,
      overdueAmount: overdue.reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
    };
  }, [taxLiabilities]);

  // Budget tracking (estimated based on compensation data)
  const budgetData = useMemo(() => {
    const annualBudget = compensation.reduce((sum, c) => {
      const annual = c.payType === 'salary' ? c.payRate : c.payRate * 2080; // 2080 hours/year
      return sum + annual;
    }, 0);
    
    const ytdSpent = payslips
      .filter(p => p.year === selectedYear)
      .reduce((sum, p) => sum + (Number(p.grossPay) || 0), 0);
    
    const monthsElapsed = new Date().getMonth() + 1;
    const expectedSpent = (annualBudget / 12) * monthsElapsed;
    const variance = ytdSpent - expectedSpent;
    const percentUsed = annualBudget > 0 ? (ytdSpent / annualBudget) * 100 : 0;

    return {
      annualBudget,
      ytdSpent,
      expectedSpent,
      variance,
      percentUsed,
      remaining: annualBudget - ytdSpent
    };
  }, [compensation, payslips, selectedYear]);

  const handleExport = (type) => {
    let data, filename;
    
    switch (type) {
      case 'summary':
        data = [{
          'Total Gross Pay': totals.grossPay,
          'Total Net Pay': totals.netPay,
          'Total Taxes': totals.totalTaxes,
          'Employer Taxes': totals.employerTaxes,
          'Total Benefits': totals.totalPreTax,
          'Total Bonuses': totals.totalBonuses,
          'Total Labor Cost': totals.totalLaborCost,
          'Employee Count': totals.employeeCount
        }];
        filename = `Finance_Summary_${selectedYear}`;
        break;
      case 'monthly':
        data = monthlyData.map(m => ({
          Month: m.fullMonth,
          'Gross Pay': m.grossPay,
          'Taxes': m.taxes,
          'Benefits': m.benefits,
          'Employer Taxes': m.employerTaxes,
          'Total Cost': m.totalCost
        }));
        filename = `Monthly_Expenses_${selectedYear}`;
        break;
      case 'department':
        data = departmentBreakdown.map(d => ({
          Department: d.name,
          'Gross Pay': d.gross,
          'Net Pay': d.net,
          'Taxes': d.taxes,
          'Headcount': d.headcount,
          'Avg Salary': d.avgSalary
        }));
        filename = `Department_Costs_${selectedYear}`;
        break;
      case 'balanceSheet':
        const assets = chartOfAccounts.filter(a => a.type === 'Asset');
        const liabilities = chartOfAccounts.filter(a => a.type === 'Liability');
        const equity = chartOfAccounts.filter(a => a.type === 'Equity');
        const totalAssets = assets.reduce((s, a) => s + a.balance, 0);
        const totalLiabilities = liabilities.reduce((s, a) => s + a.balance, 0) + taxSummary.pendingAmount;
        const totalEquity = equity.reduce((s, a) => s + a.balance, 0);
        data = [
          ...assets.map(a => ({ Section: 'Assets', Account: a.name, 'Account Number': a.number, Balance: a.balance })),
          { Section: 'Assets', Account: 'TOTAL ASSETS', 'Account Number': '', Balance: totalAssets },
          ...liabilities.map(a => ({ Section: 'Liabilities', Account: a.name, 'Account Number': a.number, Balance: a.balance })),
          { Section: 'Liabilities', Account: 'Pending Tax Liabilities', 'Account Number': '', Balance: taxSummary.pendingAmount },
          { Section: 'Liabilities', Account: 'TOTAL LIABILITIES', 'Account Number': '', Balance: totalLiabilities },
          ...equity.map(a => ({ Section: 'Equity', Account: a.name, 'Account Number': a.number, Balance: a.balance })),
          { Section: 'Equity', Account: 'TOTAL EQUITY', 'Account Number': '', Balance: totalEquity },
          { Section: 'Summary', Account: 'Total Liabilities + Equity', 'Account Number': '', Balance: totalLiabilities + totalEquity }
        ];
        filename = `Balance_Sheet_${selectedYear}`;
        break;
      case 'cashFlow':
        const cashInflows = totals.grossPay * 1.5;
        const cashOutflows = totals.netPay + totals.totalTaxes;
        const netCashFlow = cashInflows - cashOutflows;
        data = [
          { Category: 'Operating Activities', Item: 'Revenue (Est.)', Amount: cashInflows },
          { Category: 'Operating Activities', Item: 'Payroll Expenses', Amount: -totals.netPay },
          { Category: 'Operating Activities', Item: 'Tax Payments', Amount: -totals.totalTaxes },
          { Category: 'Operating Activities', Item: 'Net Cash from Operations', Amount: netCashFlow },
          { Category: 'Summary', Item: 'Total Cash Inflows', Amount: cashInflows },
          { Category: 'Summary', Item: 'Total Cash Outflows', Amount: cashOutflows },
          { Category: 'Summary', Item: 'Net Cash Flow', Amount: netCashFlow }
        ];
        filename = `Cash_Flow_Statement_${selectedYear}`;
        break;
      case 'generalLedger':
        data = chartOfAccounts.map(a => ({
          'Account Number': a.number,
          'Account Name': a.name,
          'Account Type': a.type,
          'Balance': a.balance,
          'Description': a.description || ''
        }));
        filename = `General_Ledger_${selectedYear}`;
        break;
      default:
        return;
    }
    
    exportToCSV(data, filename);
    toast.success('Report exported successfully');
  };

  // Invoice handlers
  const handleSaveInvoice = () => {
    if (!newInvoice.customer || !newInvoice.amount) {
      toast.error('Please fill in customer and amount');
      return;
    }
    const invoice = {
      id: Date.now(),
      ...newInvoice,
      amount: parseFloat(newInvoice.amount) || 0,
      createdAt: Date.now()
    };
    setInvoices(prev => [...prev, invoice]);
    setNewInvoice({
      customer: '',
      amount: '',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      description: '',
      status: 'pending'
    });
    setShowInvoiceDialog(false);
    toast.success('Invoice created successfully');
  };

  const handleDeleteInvoice = (id) => {
    setInvoices(prev => prev.filter(inv => inv.id !== id));
    toast.success('Invoice deleted');
  };

  // Bill handlers
  const handleSaveBill = () => {
    if (!newBill.vendor || !newBill.amount) {
      toast.error('Please fill in vendor and amount');
      return;
    }
    const bill = {
      id: Date.now(),
      ...newBill,
      amount: parseFloat(newBill.amount) || 0,
      createdAt: Date.now()
    };
    setBills(prev => [...prev, bill]);
    setNewBill({
      vendor: '',
      amount: '',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      description: '',
      category: '',
      status: 'pending'
    });
    setShowBillDialog(false);
    toast.success('Bill recorded successfully');
  };

  const handleDeleteBill = (id) => {
    setBills(prev => prev.filter(bill => bill.id !== id));
    toast.success('Bill deleted');
  };

  // Account handlers
  const handleSaveAccount = () => {
    if (!newAccount.id || !newAccount.name || !newAccount.type) {
      toast.error('Please fill in account number, name, and type');
      return;
    }
    
    if (editingAccount) {
      setChartOfAccounts(prev => prev.map(acc => 
        acc.id === editingAccount.id ? { ...newAccount } : acc
      ));
      toast.success('Account updated successfully');
    } else {
      if (chartOfAccounts.some(acc => acc.id === newAccount.id)) {
        toast.error('Account number already exists');
        return;
      }
      setChartOfAccounts(prev => [...prev, { ...newAccount, balance: parseFloat(newAccount.balance) || 0 }].sort((a, b) => a.id.localeCompare(b.id)));
      toast.success('Account added successfully');
    }
    
    setNewAccount({ id: '', name: '', type: 'Asset', subtype: '', balance: 0 });
    setEditingAccount(null);
    setShowAccountDialog(false);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setNewAccount({ ...account });
    setShowAccountDialog(true);
  };

  const handleDeleteAccount = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Account',
      description: 'Are you sure you want to delete this account?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (confirmed) {
      setChartOfAccounts(prev => prev.filter(acc => acc.id !== id));
      toast.success('Account deleted');
    }
  };

  // Filter accounts based on search
  const filteredAccounts = chartOfAccounts.filter(acc =>
    acc.name.toLowerCase().includes(accountSearch.toLowerCase()) ||
    acc.id.includes(accountSearch)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-teal-600" />
            Finance Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Complete expense tracking and financial analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border rounded-lg p-1">
            <Button variant="ghost" size="icon" onClick={() => setSelectedYear(selectedYear - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-bold text-lg px-2">{selectedYear}</span>
            <Button variant="ghost" size="icon" onClick={() => setSelectedYear(selectedYear + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Full Year</SelectItem>
              <SelectItem value="1">Q1</SelectItem>
              <SelectItem value="2">Q2</SelectItem>
              <SelectItem value="3">Q3</SelectItem>
              <SelectItem value="4">Q4</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => loadData()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tax Alert */}
      {taxSummary.overdueCount > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Overdue Tax Payments</AlertTitle>
          <AlertDescription className="text-red-700">
            You have {taxSummary.overdueCount} overdue tax payment(s) totaling {formatUSDCurrency(taxSummary.overdueAmount)}. 
            Please process these immediately to avoid penalties.
          </AlertDescription>
        </Alert>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Total Labor Cost</p>
                <p className="text-3xl font-bold mt-1">{formatUSDCurrency(totals.totalLaborCost)}</p>
                <p className="text-teal-200 text-xs mt-1">Gross + Employer Taxes</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gross Payroll</p>
                <p className="text-2xl font-bold text-gray-900">{formatUSDCurrency(totals.grossPay)}</p>
                <p className="text-xs text-gray-500 mt-1">{totals.payrollCount} payslips</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tax Withholdings</p>
                <p className="text-2xl font-bold text-red-600">{formatUSDCurrency(totals.totalTaxes)}</p>
                <p className="text-xs text-gray-500 mt-1">+ {formatUSDCurrency(totals.employerTaxes)} employer</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Landmark className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Benefits Cost</p>
                <p className="text-2xl font-bold text-violet-600">{formatUSDCurrency(totals.totalPreTax)}</p>
                <p className="text-xs text-gray-500 mt-1">Pre-tax deductions</p>
              </div>
              <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                <BriefcaseBusiness className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget Progress */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-600" />
                Budget Tracking
              </CardTitle>
              <CardDescription>Annual payroll budget utilization</CardDescription>
            </div>
            <Badge variant={budgetData.variance > 0 ? 'destructive' : 'default'} className="text-sm">
              {budgetData.variance > 0 ? 'Over Budget' : 'Under Budget'}: {formatUSDCurrency(Math.abs(budgetData.variance))}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">YTD Spent: {formatUSDCurrency(budgetData.ytdSpent)}</span>
              <span className="text-gray-600">Annual Budget: {formatUSDCurrency(budgetData.annualBudget)}</span>
            </div>
            <Progress value={Math.min(budgetData.percentUsed, 100)} className="h-3" />
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{budgetData.percentUsed.toFixed(1)}% used</span>
              <span className="text-gray-500">Remaining: {formatUSDCurrency(budgetData.remaining)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-gray-100/80 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="overview">Dashboard</TabsTrigger>
          <TabsTrigger value="accounts">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="invoices">Invoices & Bills</TabsTrigger>
          <TabsTrigger value="breakdown">Expense Breakdown</TabsTrigger>
          <TabsTrigger value="department">By Department</TabsTrigger>
          <TabsTrigger value="taxes">Tax Liabilities</TabsTrigger>
          <TabsTrigger value="reports">Financial Reports</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Monthly Expense Trend</CardTitle>
                  <CardDescription>Total labor cost by month</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleExport('monthly')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                    <Legend />
                    <Area type="monotone" dataKey="grossPay" stackId="1" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.6} name="Gross Pay" />
                    <Area type="monotone" dataKey="employerTaxes" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Employer Taxes" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm bg-emerald-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  <div>
                    <p className="text-sm text-emerald-600">Net Pay Disbursed</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatUSDCurrency(totals.netPay)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Clock className="w-10 h-10 text-amber-600" />
                  <div>
                    <p className="text-sm text-amber-600">Pending Tax Payments</p>
                    <p className="text-2xl font-bold text-amber-700">{formatUSDCurrency(taxSummary.pendingAmount)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-violet-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Users className="w-10 h-10 text-violet-600" />
                  <div>
                    <p className="text-sm text-violet-600">Employees on Payroll</p>
                    <p className="text-2xl font-bold text-violet-700">{totals.employeeCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Expense Breakdown Tab */}
        <TabsContent value="breakdown" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Expense Distribution</CardTitle>
                <CardDescription>Breakdown by category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={expenseBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                      >
                        {expenseBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                      <Legend />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Expense Summary</CardTitle>
                <CardDescription>Detailed cost breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expenseBreakdown.map((expense, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: expense.color }} />
                        <span className="font-medium text-sm">{expense.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">{formatUSDCurrency(expense.value)}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({((expense.value / totals.totalLaborCost) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-3 bg-teal-50 rounded-lg border border-teal-200 mt-4">
                    <span className="font-semibold">Total Expenses</span>
                    <span className="text-xl font-bold text-teal-600">
                      {formatUSDCurrency(expenseBreakdown.reduce((sum, e) => sum + e.value, 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Expense Bar Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Monthly Expense Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                    <Legend />
                    <Bar dataKey="grossPay" fill="#14b8a6" name="Gross Pay" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="taxes" fill="#ef4444" name="Taxes" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="benefits" fill="#8b5cf6" name="Benefits" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Department Tab */}
        <TabsContent value="department" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Department Cost Analysis</CardTitle>
                  <CardDescription>Payroll expenses by department</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleExport('department')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Department</TableHead>
                    <TableHead className="text-center">Headcount</TableHead>
                    <TableHead className="text-right">Gross Pay</TableHead>
                    <TableHead className="text-right">Taxes</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead className="text-right">Avg/Employee</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departmentBreakdown.map((dept, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                          <span className="font-medium">{dept.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{dept.headcount}</TableCell>
                      <TableCell className="text-right font-semibold">{formatUSDCurrency(dept.gross)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(dept.taxes)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{formatUSDCurrency(dept.net)}</TableCell>
                      <TableCell className="text-right text-gray-600">{formatUSDCurrency(dept.avgSalary)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Department Pie Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Cost Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={departmentBreakdown}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="gross"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {departmentBreakdown.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Headcount vs Cost</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={departmentBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={100} fontSize={12} />
                      <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                      <Bar dataKey="gross" fill="#14b8a6" name="Gross Pay" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tax Liabilities Tab */}
        <TabsContent value="taxes" className="space-y-6">
          {/* Tax Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Pending Payments</p>
                    <p className="text-2xl font-bold text-amber-700">{formatUSDCurrency(taxSummary.pendingAmount)}</p>
                    <p className="text-xs text-amber-600 mt-1">{taxSummary.pendingCount} payment(s)</p>
                  </div>
                  <Clock className="w-10 h-10 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-emerald-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">Paid This Year</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatUSDCurrency(taxSummary.paidAmount)}</p>
                    <p className="text-xs text-emerald-600 mt-1">{taxSummary.paidCount} payment(s)</p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                </div>
              </CardContent>
            </Card>

            <Card className={`border-0 shadow-sm ${taxSummary.overdueCount > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${taxSummary.overdueCount > 0 ? 'text-red-600' : 'text-gray-600'}`}>Overdue</p>
                    <p className={`text-2xl font-bold ${taxSummary.overdueCount > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                      {formatUSDCurrency(taxSummary.overdueAmount)}
                    </p>
                    <p className={`text-xs mt-1 ${taxSummary.overdueCount > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {taxSummary.overdueCount} payment(s)
                    </p>
                  </div>
                  <AlertTriangle className={`w-10 h-10 ${taxSummary.overdueCount > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tax Liabilities Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Upcoming Tax Payments</CardTitle>
              <CardDescription>Tax liabilities requiring payment</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Tax Type</TableHead>
                    <TableHead>Jurisdiction</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxLiabilities
                    .filter(t => t.status === 'pending')
                    .sort((a, b) => a.dueDate - b.dueDate)
                    .slice(0, 10)
                    .map((tax, idx) => {
                      const isOverdue = tax.dueDate < Date.now();
                      return (
                        <TableRow key={idx} className={isOverdue ? 'bg-red-50' : ''}>
                          <TableCell className="font-medium capitalize">
                            {tax.taxType === 'fica' ? 'FICA (SS + Medicare)' : tax.taxType}
                          </TableCell>
                          <TableCell className="capitalize">
                            {tax.jurisdiction}
                            {tax.state && ` - ${tax.state}`}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatUSDCurrency(tax.amount)}
                          </TableCell>
                          <TableCell>
                            {new Date(tax.dueDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                              {isOverdue ? 'Overdue' : 'Pending'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {taxLiabilities.filter(t => t.status === 'pending').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No pending tax payments
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chart of Accounts Tab */}
        <TabsContent value="accounts" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-teal-600" />
                    Chart of Accounts
                  </CardTitle>
                  <CardDescription>Manage your accounting structure</CardDescription>
                </div>
                <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => {
                  setEditingAccount(null);
                  setNewAccount({ id: '', name: '', type: 'Asset', subtype: '', balance: 0 });
                  setShowAccountDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    placeholder="Search accounts..." 
                    className="pl-9 max-w-sm" 
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account #</TableHead>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Subtype</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map(account => (
                    <TableRow key={account.id}>
                      <TableCell className="font-mono">{account.id}</TableCell>
                      <TableCell className="font-medium">{account.name}</TableCell>
                      <TableCell>
                        <Badge className={
                          account.type === 'Asset' ? 'bg-blue-100 text-blue-700' :
                          account.type === 'Liability' ? 'bg-red-100 text-red-700' :
                          account.type === 'Equity' ? 'bg-purple-100 text-purple-700' :
                          account.type === 'Income' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {account.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500">{account.subtype}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatUSDCurrency(account.balance)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditAccount(account)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteAccount(account.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-teal-600" />
                    General Journal
                  </CardTitle>
                  <CardDescription>Record and view all transactions</CardDescription>
                </div>
                <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowTransactionDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" /> New Journal Entry
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Auto-generated payroll transactions */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Recent Payroll Transactions</h4>
                <div className="text-sm text-gray-500 mb-4">
                  Transactions are automatically created from payroll runs
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollRuns.slice(0, 5).map((run, idx) => (
                    <React.Fragment key={run.id || idx}>
                      <TableRow className="bg-gray-50">
                        <TableCell>{new Date(run.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell className="font-medium">Payroll - {run.periodName || run.month + '/' + run.year}</TableCell>
                        <TableCell>Salaries & Wages (5000)</TableCell>
                        <TableCell className="text-right font-semibold text-blue-600">
                          {formatUSDCurrency(run.totals?.totalGross || 0)}
                        </TableCell>
                        <TableCell className="text-right">-</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell className="text-gray-500 text-sm pl-8">Payroll Taxes Expense</TableCell>
                        <TableCell className="text-gray-500">Payroll Taxes (5100)</TableCell>
                        <TableCell className="text-right">{formatUSDCurrency(run.totals?.totalTax || 0)}</TableCell>
                        <TableCell className="text-right">-</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell className="text-gray-500 text-sm pl-8">Cash Payment</TableCell>
                        <TableCell className="text-gray-500">Cash (1000)</TableCell>
                        <TableCell className="text-right">-</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatUSDCurrency(run.totals?.totalNet || 0)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell className="text-gray-500 text-sm pl-8">Tax Payable</TableCell>
                        <TableCell className="text-gray-500">Payroll Liabilities (2200)</TableCell>
                        <TableCell className="text-right">-</TableCell>
                        <TableCell className="text-right">{formatUSDCurrency(run.totals?.totalTax || 0)}</TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  {payrollRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No transactions recorded yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices & Bills Tab */}
        <TabsContent value="invoices" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Accounts Receivable */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                      Invoices (Receivables)
                    </CardTitle>
                    <CardDescription>Money owed to you</CardDescription>
                  </div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowInvoiceDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" /> New Invoice
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No invoices created yet</p>
                      <p className="text-sm mt-1">Create invoices to track money owed to you</p>
                    </div>
                  ) : (
                    invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium">{inv.customer}</p>
                          <p className="text-sm text-gray-500">Due: {inv.dueDate}</p>
                          {inv.description && <p className="text-xs text-gray-400">{inv.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-emerald-100 text-emerald-700">{formatUSDCurrency(inv.amount)}</Badge>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteInvoice(inv.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Outstanding</span>
                  <span className="text-lg font-bold text-emerald-600">
                    {formatUSDCurrency(invoices.reduce((sum, i) => sum + (i.amount || 0), 0))}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Accounts Payable */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ArrowDownRight className="w-5 h-5 text-red-600" />
                      Bills (Payables)
                    </CardTitle>
                    <CardDescription>Money you owe</CardDescription>
                  </div>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setShowBillDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" /> New Bill
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {bills.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No bills recorded yet</p>
                      <p className="text-sm mt-1">Track bills and expenses you need to pay</p>
                    </div>
                  ) : (
                    bills.map(bill => (
                      <div key={bill.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium">{bill.vendor}</p>
                          <p className="text-sm text-gray-500">Due: {bill.dueDate}</p>
                          {bill.category && <p className="text-xs text-gray-400">{bill.category}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-100 text-red-700">{formatUSDCurrency(bill.amount)}</Badge>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteBill(bill.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Payable</span>
                  <span className="text-lg font-bold text-red-600">
                    {formatUSDCurrency(bills.reduce((sum, b) => sum + (b.amount || 0), 0))}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Profit & Loss</h3>
                    <p className="text-sm text-gray-500">Income vs Expenses</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Revenue</span>
                    <span className="text-emerald-600 font-medium">{formatUSDCurrency(totals.totalBonuses * 10)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Expenses</span>
                    <span className="text-red-600 font-medium">{formatUSDCurrency(totals.totalLaborCost)}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>Net Income</span>
                    <span className={totals.totalBonuses * 10 - totals.totalLaborCost > 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {formatUSDCurrency((totals.totalBonuses * 10) - totals.totalLaborCost)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Scale className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Balance Sheet</h3>
                    <p className="text-sm text-gray-500">Assets & Liabilities</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Assets</span>
                    <span className="text-blue-600 font-medium">
                      {formatUSDCurrency(chartOfAccounts.filter(a => a.type === 'Asset').reduce((s, a) => s + a.balance, 0))}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Total Liabilities</span>
                    <span className="text-red-600 font-medium">
                      {formatUSDCurrency(chartOfAccounts.filter(a => a.type === 'Liability').reduce((s, a) => s + a.balance, 0) + taxSummary.pendingAmount)}
                    </span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>Equity</span>
                    <span className="text-purple-600">
                      {formatUSDCurrency(chartOfAccounts.filter(a => a.type === 'Equity').reduce((s, a) => s + a.balance, 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Banknote className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Cash Flow</h3>
                    <p className="text-sm text-gray-500">Money in & out</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Cash Inflows</span>
                    <span className="text-emerald-600 font-medium">{formatUSDCurrency(totals.grossPay * 1.5)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Cash Outflows</span>
                    <span className="text-red-600 font-medium">{formatUSDCurrency(totals.netPay + totals.totalTaxes)}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>Net Cash Flow</span>
                    <span className="text-emerald-600">
                      {formatUSDCurrency((totals.grossPay * 1.5) - (totals.netPay + totals.totalTaxes))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Generate Reports</CardTitle>
              <CardDescription>Download detailed financial reports</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport('summary')}>
                  <FileText className="w-6 h-6 mb-2" />
                  <span>Profit & Loss</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport('balanceSheet')}>
                  <Building className="w-6 h-6 mb-2" />
                  <span>Balance Sheet</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport('cashFlow')}>
                  <Banknote className="w-6 h-6 mb-2" />
                  <span>Cash Flow</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport('generalLedger')}>
                  <BookOpen className="w-6 h-6 mb-2" />
                  <span>General Ledger</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Journal Entry Dialog */}
      <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Journal Entry</DialogTitle>
            <DialogDescription>Record a new transaction with double-entry bookkeeping</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newTransaction.date}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Reference #</Label>
                <Input placeholder="JE-001" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Enter transaction description"
                value={newTransaction.description}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <Separator />
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-2 text-sm font-medium text-gray-500">
                <div className="col-span-2">Account</div>
                <div>Debit</div>
                <div>Credit</div>
              </div>
              {newTransaction.entries.map((entry, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-2">
                  <Select
                    value={entry.account}
                    onValueChange={(val) => {
                      const entries = [...newTransaction.entries];
                      entries[idx].account = val;
                      setNewTransaction(prev => ({ ...prev, entries }));
                    }}
                  >
                    <SelectTrigger className="col-span-2">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {chartOfAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>{acc.id} - {acc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={entry.debit || ''}
                    onChange={(e) => {
                      const entries = [...newTransaction.entries];
                      entries[idx].debit = parseFloat(e.target.value) || 0;
                      setNewTransaction(prev => ({ ...prev, entries }));
                    }}
                  />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={entry.credit || ''}
                    onChange={(e) => {
                      const entries = [...newTransaction.entries];
                      entries[idx].credit = parseFloat(e.target.value) || 0;
                      setNewTransaction(prev => ({ ...prev, entries }));
                    }}
                  />
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setNewTransaction(prev => ({
                  ...prev,
                  entries: [...prev.entries, { account: '', debit: 0, credit: 0 }]
                }))}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Line
              </Button>
            </div>
            <div className="flex justify-between pt-2 border-t font-medium">
              <span>Total</span>
              <div className="flex gap-8">
                <span className="text-blue-600">
                  Debit: {formatUSDCurrency(newTransaction.entries.reduce((s, e) => s + (e.debit || 0), 0))}
                </span>
                <span className="text-green-600">
                  Credit: {formatUSDCurrency(newTransaction.entries.reduce((s, e) => s + (e.credit || 0), 0))}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransactionDialog(false)}>Cancel</Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => {
                const totalDebit = newTransaction.entries.reduce((s, e) => s + (e.debit || 0), 0);
                const totalCredit = newTransaction.entries.reduce((s, e) => s + (e.credit || 0), 0);
                if (Math.abs(totalDebit - totalCredit) > 0.01) {
                  toast.error('Debits must equal credits');
                  return;
                }
                setTransactions(prev => [...prev, { ...newTransaction, id: Date.now() }]);
                setShowTransactionDialog(false);
                setNewTransaction({
                  date: new Date().toISOString().split('T')[0],
                  description: '',
                  entries: [{ account: '', debit: 0, credit: 0 }, { account: '', debit: 0, credit: 0 }]
                });
                toast.success('Journal entry saved');
              }}
            >
              Save Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Options */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-teal-600" />
            Export Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => handleExport('summary')}>
              <Download className="w-4 h-4 mr-2" />
              Financial Summary
            </Button>
            <Button variant="outline" onClick={() => handleExport('monthly')}>
              <Download className="w-4 h-4 mr-2" />
              Monthly Report
            </Button>
            <Button variant="outline" onClick={() => handleExport('department')}>
              <Download className="w-4 h-4 mr-2" />
              Department Costs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* New Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-emerald-600" />
              Create New Invoice
            </DialogTitle>
            <DialogDescription>Record a new invoice for accounts receivable</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                placeholder="Enter customer name"
                value={newInvoice.customer}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, customer: e.target.value }))}
              />
            </div>
            <div>
              <Label>Amount *</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={newInvoice.amount}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={newInvoice.dueDate}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Invoice description"
                value={newInvoice.description}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveInvoice}>
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Bill Dialog */}
      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5 text-red-600" />
              Record New Bill
            </DialogTitle>
            <DialogDescription>Record a new bill for accounts payable</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Vendor Name *</Label>
              <Input
                placeholder="Enter vendor name"
                value={newBill.vendor}
                onChange={(e) => setNewBill(prev => ({ ...prev, vendor: e.target.value }))}
              />
            </div>
            <div>
              <Label>Amount *</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={newBill.amount}
                onChange={(e) => setNewBill(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={newBill.dueDate}
                onChange={(e) => setNewBill(prev => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newBill.category} onValueChange={(v) => setNewBill(prev => ({ ...prev, category: v }))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="utilities">Utilities</SelectItem>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="supplies">Supplies</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="services">Professional Services</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Bill description"
                value={newBill.description}
                onChange={(e) => setNewBill(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBillDialog(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleSaveBill}>
              Record Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Dialog */}
      <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-600" />
              {editingAccount ? 'Edit Account' : 'Add New Account'}
            </DialogTitle>
            <DialogDescription>
              {editingAccount ? 'Update account details' : 'Create a new account in your chart of accounts'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Account Number *</Label>
              <Input
                placeholder="e.g., 1000"
                value={newAccount.id}
                onChange={(e) => setNewAccount(prev => ({ ...prev, id: e.target.value }))}
                disabled={!!editingAccount}
              />
            </div>
            <div>
              <Label>Account Name *</Label>
              <Input
                placeholder="e.g., Cash"
                value={newAccount.name}
                onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Account Type *</Label>
              <Select value={newAccount.type} onValueChange={(v) => setNewAccount(prev => ({ ...prev, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asset">Asset</SelectItem>
                  <SelectItem value="Liability">Liability</SelectItem>
                  <SelectItem value="Equity">Equity</SelectItem>
                  <SelectItem value="Income">Income</SelectItem>
                  <SelectItem value="Expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subtype</Label>
              <Input
                placeholder="e.g., Current Asset"
                value={newAccount.subtype}
                onChange={(e) => setNewAccount(prev => ({ ...prev, subtype: e.target.value }))}
              />
            </div>
            <div>
              <Label>Opening Balance</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={newAccount.balance}
                onChange={(e) => setNewAccount(prev => ({ ...prev, balance: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAccountDialog(false);
              setEditingAccount(null);
              setNewAccount({ id: '', name: '', type: 'Asset', subtype: '', balance: 0 });
            }}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSaveAccount}>
              {editingAccount ? 'Update Account' : 'Add Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
